/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { CustomLink } from "@/components/links/nav-link"
import { useNavigationWithSearch } from "@/hooks/useNavigationWithSearch"
import { useSingleFilterParam } from "@/hooks/useSingleFilterParam"
import type { PaginationProps } from "antd"
import { Checkbox, ConfigProvider, Empty, Pagination, Select, Spin } from "antd"
import { FC, useEffect, useRef, useState } from "react"
import { CSVLink } from "react-csv"
import { CSSTransition, SwitchTransition } from "react-transition-group"
import { useAuth } from "../../../../../../../components/auth"
import { useGetTeamFiltersLazyQuery } from "../../../../../../../graphql"
import { DownloadIcon, EditDots, PlusIcon, SearchIcon } from "../../../../../../../icons"
import { getArrayForm, handleToggleAll, removeSelectDuplicates } from "../../../../../../../utils"
import "./index.less"

const List: FC<RoleListInterface> = ({
  list,
  page,
  loading,
  setPage,
  filters,
  pageSize,
  getStatus,
  setFilters,
  totalItems,
  setPageSize,
  exportLoading,
  listForExport,
  downloadBtnRef,
  createExportList,
}) => {
  const { permissions } = useAuth()
  const blockPermissions = permissions?.firstMenu
    ?.find(item => item.name == "Team")
    // eslint-disable-next-line
    ?.blocks?.find((block: any) => block.name == "Salon")

  const { navigateWithSearch } = useNavigationWithSearch()

  const [getTeamFilters] = useGetTeamFiltersLazyQuery()
  const { value: usersValue, setValue: setUsersValue } = useSingleFilterParam("users")

  const [names, setNames] = useState<SelectItems>()
  const [emails, setEmails] = useState<SelectItems>()
  const [salesManagers, setSalesManagers] = useState<SelectItems>()
  const [distributors, setDistributors] = useState<SelectItems>()

  // eslint-disable-next-line
  const refFiltersDataSet = useRef<null | any[] | undefined>(null)

  const selectedUsers = getArrayForm(usersValue)

  const onChangePage: PaginationProps["onChange"] = (pageNumber: number) => {
    setPage(pageNumber)
  }

  const onChangePageSize: PaginationProps["onShowSizeChange"] = (_, pageSize: number) => {
    setPageSize(pageSize)
  }

  // eslint-disable-next-line
  function filterData(dataArray: any[], filters: any) {
    const keyMapping: { [key: string]: string } = {
      selectedName: "name",
      selectedEmail: "email",
      selectedSM: "smName",
      selectedInsiderStatus: "insiderPermission",
      selectedDistributor: "dName",
      selectedRegion: "region",
    }

    const result = dataArray.filter(item =>
      Object.keys(filters).every(filterKey => {
        const filterValue = filters[filterKey]

        if (filterKey === "selectedStatus") {
          const statuses = filterValue as string[]
          if (!statuses || statuses.length === 0 || statuses.includes("All")) return true
          return statuses.some(status => +item?.[status.toLowerCase()] === 1)
        }

        const dataKey = keyMapping[filterKey] || filterKey
        if (filterValue === "All") return true
        if (filterKey === "selectedInsiderStatus") {
          if (filterValue === "Allowed") {
            return +item[dataKey] === 1
          } else {
            return +item[dataKey] === 0
          }
        }

        return item?.[dataKey] && typeof item[dataKey] === "string"
          ? item?.[dataKey]?.includes(filterValue)
          : +item?.[dataKey] === 1
      })
    )

    return result
  }

  const getFilters = async () => {
    let initFilters = refFiltersDataSet.current

    if (!initFilters) {
      const newFilters = await getTeamFilters({
        variables: { input: { type: "salon" } },
        fetchPolicy: "no-cache",
        errorPolicy: "all",
      })

      refFiltersDataSet.current = newFilters.data?.getTeamFilters?.filters
      initFilters = refFiltersDataSet.current
    }

    if (initFilters) {
      const result = filterData(initFilters, filters)

      setNames(
        removeSelectDuplicates(
          result
            .map(f => ({
              value: f?.name || "",
              label: f?.name || "",
              key: f?.name || "",
            }))
            // eslint-disable-next-line
            .filter((item: any) => item.value)!
        )
      )
      setEmails(
        removeSelectDuplicates(
          result
            .map(f => ({
              value: f?.email || "",
              label: f?.email || "",
              key: f?.email || "",
            }))
            // eslint-disable-next-line
            .filter((item: any) => item.value)!
        )
      )
      setDistributors(
        removeSelectDuplicates(
          result
            .map(f => ({
              value: f?.dName || "",
              label: f?.dName || "",
              key: f?.dName || "",
            }))
            // eslint-disable-next-line
            .filter((item: any) => item.value)
        )!
      )
      setSalesManagers(
        removeSelectDuplicates(
          result
            .map(f => ({
              value: f?.smName || "",
              label: f?.smName || "",
              key: f?.smName || "",
            }))
            // eslint-disable-next-line
            .filter((item: any) => item.value)
        )!
      )
    }
  }

  useEffect(() => {
    getFilters()
    // eslint-disable-next-line
  }, [filters])

  const filteredOption = (input: string, option: { label: string; value: string; key?: number }) =>
    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())

  const handleFilter = (data: string, type: string) => {
    const handleFilterChange = (valueType: string) =>
      setFilters({
        ...filters,
        [valueType]: data,
      })

    switch (type) {
      case "name":
        handleFilterChange("selectedName")
        break
      case "email":
        handleFilterChange("selectedEmail")
        break
      case "sales-manager":
        handleFilterChange("selectedSM")
        break
      case "distributor":
        handleFilterChange("selectedDistributor")
        break
    }
  }

  const createSelectElement = (type: string, name: string, loading: boolean, options: SelectItems) => (
    <div
      className={`${type} ${
        ((type == "name" && filters?.selectedName !== "All") ||
          (type == "email" && filters?.selectedEmail !== "All") ||
          (type == "sales-manager" && filters?.salesManager !== "All") ||
          (type == "distributor" && filters?.distributor !== "All")) &&
        "active"
      }`}
      children={
        <Select
          showSearch
          value={name}
          options={options}
          optionFilterProp={"children"}
          defaultActiveFirstOption={true}
          // eslint-disable-next-line
          filterOption={filteredOption as any}
          onChange={data => handleFilter(data, type)}
          notFoundContent={
            loading || !options ? (
              <div className={"no-data"} children={<Spin />} />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} className={"no-data"} />
            )
          }
          suffixIcon={<SearchIcon />}
        />
      }
    />
  )

  const handleCreateNewClick = () => navigateWithSearch("/account/team/salon/new-salon")

  const handleToggleUser = (id: string) => {
    const currentValues = Array.isArray(usersValue) ? usersValue : usersValue ? [usersValue] : []

    const newValue = currentValues.includes(id) ? currentValues.filter(item => item !== id) : [...currentValues, id]

    setUsersValue(newValue.length > 0 ? newValue : null)
  }

  const ownerEntries: { id: string }[] = (list || [])
    // eslint-disable-next-line
    .map((item: any) => ({ id: item?.attributes?.owner?.data?.id as string }))
    .filter((entry: { id: string }) => Boolean(entry.id))

  const handleAllUsers = () => handleToggleAll(selectedUsers, setUsersValue, ownerEntries)

  const isAllUsersSelected =
    !!ownerEntries.length && ownerEntries.every((entry: { id: string }) => selectedUsers.includes(entry.id))

  return (
    <SwitchTransition mode={"out-in"}>
      <CSSTransition
        timeout={250}
        appear={true}
        unmountOnExit
        classNames={"fade"}
        key={JSON.stringify(filters) + String(page) + String(pageSize)}
      >
        <div className={"main-list-wrapper salons-list"}>
          <ConfigProvider
            theme={{
              components: {
                Pagination: {
                  colorPrimary: "black",
                  colorPrimaryBorder: "black",
                  colorPrimaryHover: "black",
                  controlOutline: "#fff",
                },
                Select: {
                  colorText: "#677685",
                  colorPrimaryBorder: "#fff",
                  colorPrimaryHover: "#fff",
                  controlOutline: "#fff",
                  colorBgContainerDisabled: "#fff",
                },
                Checkbox: {
                  colorBorder: "#000",
                  colorPrimaryHover: "#000",
                  colorPrimary: "#000",
                  borderRadius: 0,
                },
              },
            }}
          >
            <div className={"list-wrapper"}>
              <div className={"items top"}>
                <div className={"centered checkbox-wrapper"}>
                  <Checkbox onChange={handleAllUsers} checked={isAllUsersSelected} />
                </div>
                {createSelectElement("name", "NAME", loading, names!)}
                {createSelectElement("email", "EMAIL", loading, emails!)}
                {createSelectElement("sales-manager", "SALES MANAGER", loading, salesManagers!)}
                {createSelectElement("distributor", "DISTRIBUTOR", loading, distributors!)}
                <div children={"REGION"} />
                <div children={"STATUS"} />
                <div className={"centered"} children={"EDIT"} />
              </div>

              {list &&
                // eslint-disable-next-line
                list.map((item: any, i: number) => {
                  const ownerId = item?.attributes?.owner?.data?.id

                  return (
                    <div className={"items"} key={`list-item-${i}`}>
                      <div className={"centered checkbox-wrapper"}>
                        <Checkbox
                          disabled={!ownerId}
                          onChange={() => handleToggleUser(ownerId)}
                          checked={selectedUsers.includes(ownerId)}
                        />
                      </div>
                      <div children={<p>{item?.attributes?.name || "-"}</p>} />
                      <div children={<p>{item?.attributes?.owner?.data?.attributes?.email || "-"}</p>} />
                      <div children={<p>{item?.attributes?.sales_manager?.data?.attributes?.name || "-"}</p>} />
                      <div children={<p>{item?.attributes?.distributor?.data?.attributes?.name || "-"}</p>} />
                      <div children={<p>{item?.attributes?.region?.data?.attributes?.name || "-"}</p>} />
                      <div className={`activity ${getStatus(item)}`} children={getStatus(item)} />
                      <div
                        className={`centered ${!blockPermissions?.edit && "disabled"}`}
                        children={<CustomLink to={!blockPermissions?.edit ? "#" : item?.id} children={<EditDots />} />}
                      />
                    </div>
                  )
                })}

              {!list && (
                <div className={"spinner-wrapper"}>
                  <Spin />
                </div>
              )}

              {list?.length == 0 && (
                <div className={"empty"}>
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </div>
              )}
            </div>

            <div
              className={"navigation"}
              children={
                <Pagination
                  showQuickJumper
                  current={page}
                  pageSize={pageSize}
                  total={totalItems}
                  onChange={onChangePage}
                  onShowSizeChange={onChangePageSize}
                />
              }
            />

            {blockPermissions?.edit && (
              <button onClick={handleCreateNewClick} className={"create-new"}>
                New Salon
                <PlusIcon />
              </button>
            )}

            <button
              onClick={() => createExportList()}
              className={`download ${!blockPermissions?.edit && "single"}`}
              disabled={!list}
            >
              Download CSV
              {exportLoading ? <Spin /> : <DownloadIcon />}
            </button>

            <CSVLink
              data={listForExport || []}
              filename={"salons"}
              // eslint-disable-next-line
              ref={downloadBtnRef as any}
            />
          </ConfigProvider>
        </div>
      </CSSTransition>
    </SwitchTransition>
  )
}

export default List
